<template>
  <div class="orders-dashboard is-fullwidth">
    <h1 class="title is-4 mb-2">Sock Orders Dashboard (past 2 years)</h1>
    <p class="is-size-7 has-text-danger mb-2">The data on this page was last generated at {{ aggregatedSockOrdersLastCreated }}</p>
    
    <div class="box dashboard-filters">
      <div class="columns is-multiline">
        <div class="column is-12-tablet is-4-desktop">
          <label class="label is-size-7" for="orders-dashboard-search">Search</label>
          <input
            id="orders-dashboard-search"
            v-model.trim="filters.search"
            class="input is-small"
            type="text"
            placeholder="PO, client or garment"
          />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-status">Status</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-status" v-model="filters.status">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-region">Region</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-region" v-model="filters.region">
              <option value="">All Regions</option>
              <option v-for="region in regionOptions" :key="region" :value="region">{{ region }}</option>
            </select>
          </div>
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-client">Client</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-client" v-model="filters.clientname">
              <option value="">All Clients</option>
              <option v-for="client in clientOptions" :key="client" :value="client">{{ client }}</option>
            </select>
          </div>
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-garment">Garment</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-garment" v-model="filters.garmentname">
              <option value="">All Garments</option>
              <option v-for="garment in garmentOptions" :key="garment" :value="garment">{{ garment }}</option>
            </select>
          </div>
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-order-date-from">Order Date From</label>
          <input id="orders-dashboard-order-date-from" v-model="filters.orderDateFrom" class="input is-small" type="date" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-order-date-to">Order Date To</label>
          <input id="orders-dashboard-order-date-to" v-model="filters.orderDateTo" class="input is-small" type="date" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-delivery-date-from">Delivery Date From</label>
          <input id="orders-dashboard-delivery-date-from" v-model="filters.deliveryDateFrom" class="input is-small" type="date" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-delivery-date-to">Delivery Date To</label>
          <input id="orders-dashboard-delivery-date-to" v-model="filters.deliveryDateTo" class="input is-small" type="date" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-qty-min">Qty Min</label>
          <input id="orders-dashboard-qty-min" v-model.number="filters.qtyMin" class="input is-small" type="number" min="0" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-qty-max">Qty Max</label>
          <input id="orders-dashboard-qty-max" v-model.number="filters.qtyMax" class="input is-small" type="number" min="0" />
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-sort-field">Sort By</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-sort-field" v-model="sort.field">
              <option value="ponumber">PO Number</option>
              <option value="clientname">Client</option>
              <option value="garmentname">Garment</option>
              <option value="region">Region</option>
              <option value="orderdate">Order Date</option>
              <option value="deliverydate">Delivery Date</option>
              <option value="total_qty">Total Qty</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>

        <div class="column is-6-tablet is-2-desktop">
          <label class="label is-size-7" for="orders-dashboard-sort-direction">Sort Direction</label>
          <div class="select is-small is-fullwidth">
            <select id="orders-dashboard-sort-direction" v-model="sort.direction">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        <div class="column is-12-tablet is-2-desktop is-flex is-align-items-flex-end">
          <button class="button is-small is-light is-fullwidth" @click="resetFilters">Reset Filters</button>
        </div>
      </div>

      <div class="is-size-7 has-text-grey">
        Showing {{ filteredOrders.length }} of {{ orders.length }} orders
      </div>
    </div>

    <div v-if="orders_loading" class="notification is-info is-light">Loading orders...</div>
    <div v-else-if="load_error" class="notification is-danger is-light">{{ load_error }}</div>

    <div v-else>
      <div class="tabs is-boxed is-small">
        <ul>
          <li :class="{ 'is-active': activeTab === 'summary' }"><a @click="activeTab = 'summary'">Summary</a></li>
          <li :class="{ 'is-active': activeTab === 'orders' }"><a @click="activeTab = 'orders'">Orders</a></li>
        </ul>
      </div>

      <div v-if="activeTab === 'summary'" class="summary-tab">
        <div class="columns is-multiline">
          <div class="column is-6-tablet is-3-desktop" v-for="card in summaryCards" :key="card.label">
            <div class="box summary-card">
              <p class="is-size-7 has-text-grey">{{ card.label }}</p>
              <p class="title is-5 mb-0">{{ card.value }}</p>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-12-tablet is-3-desktop">
            <div class="box">
              <h3 class="title is-6">By Status</h3>
              <table class="table is-fullwidth is-size-7 is-striped">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Orders</th>
                    <th>Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in groupedByStatus" :key="row.key">
                    <td>{{ row.key }}</td>
                    <td>{{ row.orders }}</td>
                    <td>{{ row.total_qty }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="column is-12-tablet is-3-desktop">
            <div class="box">
              <h3 class="title is-6">By Region</h3>
              <table class="table is-fullwidth is-size-7 is-striped">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Orders</th>
                    <th>Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in groupedByRegion" :key="row.key">
                    <td>{{ row.key }}</td>
                    <td>{{ row.orders }}</td>
                    <td>{{ row.total_qty }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="column is-12-tablet is-3-desktop">
            <div class="box">
              <h3 class="title is-6">By Garment</h3>
              <table class="table is-fullwidth is-size-7 is-striped">
                <thead>
                  <tr>
                    <th>Garment</th>
                    <th>Orders</th>
                    <th>Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in groupedByGarment" :key="row.key">
                    <td>{{ row.key }}</td>
                    <td>{{ row.orders }}</td>
                    <td>{{ row.total_qty }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="column is-12-tablet is-3-desktop">
            <div class="box">
              <h3 class="title is-6">By Client</h3>
              <table class="table is-fullwidth is-size-7 is-striped">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Orders</th>
                    <th>Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in groupedByClient" :key="row.key">
                    <td>{{ row.key }}</td>
                    <td>{{ row.orders }}</td>
                    <td>{{ row.total_qty }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="orders-tab box">
        <table class="table is-fullwidth is-size-7 is-hoverable is-striped">
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Client Name</th>
              <th>Garment Name</th>
              <th>Region</th>
              <th>Order Date</th>
              <th>Delivery Date</th>
              <th>Total Qty</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredOrders.length === 0">
              <td colspan="8" class="has-text-centered">No orders match your current filters.</td>
            </tr>
            <tr v-for="order in filteredOrders" :key="order.orderID || order.ponumber">
              <td>{{ valueOrFallback(order.ponumber) }}</td>
              <td>{{ valueOrFallback(order.clientname) }}</td>
              <td>{{ valueOrFallback(order.garmentname) }}</td>
              <td>{{ valueOrFallback(order.region) }}</td>
              <td>{{ formatDate(order.orderdate) }}</td>
              <td>{{ formatDate(order.deliverydate) }}</td>
              <td>{{ toNumber(order.total_qty) }}</td>
              <td>{{ normalizedStatus(order.status) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import OrderService from '@/services/OrderService.js';

export default {
  name: 'AggregatedSockOrders',
  data() {
    return {
      orders: [],
      aggregate_table: [],
      orders_loading: false,
      load_error: '',
      accessToken: '',
      activeTab: 'summary',
      filters: {
        search: '',
        status: 'all',
        region: '',
        clientname: '',
        garmentname: '',
        orderDateFrom: '',
        orderDateTo: '',
        deliveryDateFrom: '',
        deliveryDateTo: '',
        qtyMin: null,
        qtyMax: null,
      },
      sort: {
        field: 'deliverydate',
        direction: 'asc',
      },
    };
  },
  created() {
    this.getAccessToken().then(() => {
      this.getOrders();
      this.getAggregateTable();
    });
  },
  computed: {
    aggregatedSockOrdersLastCreated() {
      const rows = Array.isArray(this.aggregate_table)
        ? this.aggregate_table
        : (this.aggregate_table && Array.isArray(this.aggregate_table.data) ? this.aggregate_table.data : []);

      const row = rows.find((item) => this.normalizeString(item.table_name).toLowerCase() === 'aggregated_sock_orders');

      if (row && row.last_created) {
        return row.last_created;
      }

      return 'N/A';
    },
    regionOptions() {
      return this.sortedUniqueValues('region');
    },
    clientOptions() {
      return this.sortedUniqueValues('clientname');
    },
    garmentOptions() {
      return this.sortedUniqueValues('garmentname');
    },
    filteredOrders() {
      const searchTerm = (this.filters.search || '').toLowerCase();

      const subset = this.orders.filter((order) => {
        const status = this.normalizedStatus(order.status);
        const qty = this.toNumber(order.total_qty);

        const matchesSearch = !searchTerm || [
          order.ponumber,
          order.clientname,
          order.garmentname,
        ].some((value) => String(value || '').toLowerCase().includes(searchTerm));

        const matchesStatus = this.matchesStatusFilter(status);
        const matchesRegion = !this.filters.region || (order.region || '') === this.filters.region;
        const matchesClient = !this.filters.clientname || (order.clientname || '') === this.filters.clientname;
        const matchesGarment = !this.filters.garmentname || (order.garmentname || '') === this.filters.garmentname;

        const matchesOrderDate = this.withinDateRange(order.orderdate, this.filters.orderDateFrom, this.filters.orderDateTo);
        const matchesDeliveryDate = this.withinDateRange(order.deliverydate, this.filters.deliveryDateFrom, this.filters.deliveryDateTo);

        const matchesQtyMin = this.filters.qtyMin === null || this.filters.qtyMin === '' || qty >= Number(this.filters.qtyMin);
        const matchesQtyMax = this.filters.qtyMax === null || this.filters.qtyMax === '' || qty <= Number(this.filters.qtyMax);

        return matchesSearch && matchesStatus && matchesRegion && matchesClient && matchesGarment && matchesOrderDate && matchesDeliveryDate && matchesQtyMin && matchesQtyMax;
      });

      return subset.sort((a, b) => this.compareOrders(a, b));
    },
    totalQty() {
      return this.filteredOrders.reduce((sum, order) => sum + this.toNumber(order.total_qty), 0);
    },
    completedOrders() {
      return this.filteredOrders.filter((order) => this.isCompleted(order.status));
    },
    notCompletedOrders() {
      return this.filteredOrders.filter((order) => !this.isCompleted(order.status));
    },
    summaryCards() {
      const completedQty = this.completedOrders.reduce((sum, order) => sum + this.toNumber(order.total_qty), 0);
      const notCompletedQty = this.notCompletedOrders.reduce((sum, order) => sum + this.toNumber(order.total_qty), 0);
      const averageQty = this.filteredOrders.length > 0 ? Math.round(this.totalQty / this.filteredOrders.length) : 0;

      return [
        { label: 'Filtered Orders', value: this.filteredOrders.length },
        { label: 'Total Qty', value: this.totalQty },
        { label: 'Completed Orders', value: this.completedOrders.length },
        { label: 'Completed Qty', value: completedQty },
        { label: 'Not Completed Orders', value: this.notCompletedOrders.length },
        { label: 'Not Completed Qty', value: notCompletedQty },
        { label: 'Average Qty / Order', value: averageQty },
      ];
    },
    groupedByRegion() {
      return this.groupByKey('region');
    },
    groupedByStatus() {
      return this.groupByKey('status');
    },
    groupedByGarment() {
      return this.groupByKey('garmentname');
    },
    groupedByClient() {
      return this.groupByKey('clientname');
    },
  },
  methods: {
    async getAccessToken() {
      this.accessToken = await this.$auth.getTokenSilently();
    },
    async getOrders() {
      this.orders_loading = true;
      this.load_error = '';

      try {
        const orders = await OrderService.getAggregatedSockOrders(this.accessToken);
        this.$set(this, 'orders', Array.isArray(orders) ? orders : []);
      } catch (error) {
        // Fallback keeps the page usable if the dedicated endpoint is unavailable.
        try {
          const fallbackOrders = await OrderService.getOrders(this.accessToken, '', '', '', '', '', 1, '');
          this.$set(this, 'orders', Array.isArray(fallbackOrders) ? fallbackOrders : []);
          this.load_error = 'Using fallback order source. Dedicated dashboard endpoint is unavailable.';
        } catch (fallbackError) {
          this.load_error = 'Unable to load orders for dashboard.';
          this.$set(this, 'orders', []);
        }
      } finally {
        this.orders_loading = false;
      }
    },
    async getAggregateTable() {
      try {
        const aggregateTable = await OrderService.getAggregateTable(this.accessToken);
        this.$set(this, 'aggregate_table', aggregateTable || []);
      } catch (error) {
        this.$set(this, 'aggregate_table', []);
      }
    },
    valueOrFallback(value) {
      if (value === null || value === undefined || String(value).trim() === '') {
        return 'N/A';
      }

      return value;
    },
    toNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeString(value) {
      if (value === null || value === undefined) {
        return '';
      }

      return String(value).trim();
    },
    normalizedStatus(status) {
      const normalized = this.normalizeString(status);
      if (!normalized) {
        return 'Unknown';
      }

      const lowered = normalized.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

      if (lowered === 'pending') {
        return 'Pending';
      }

      if (lowered === 'new') {
        return 'New';
      }

      if (lowered === 'in progress' || lowered === 'inprogress') {
        return 'In Progress';
      }

      if (lowered === 'completed') {
        return 'Completed';
      }

      if (lowered === 'cancelled' || lowered === 'canceled') {
        return 'Cancelled';
      }

      return normalized;
    },
    isCompleted(status) {
      return this.normalizedStatus(status).toLowerCase() === 'completed';
    },
    matchesStatusFilter(status) {
      if (this.filters.status === 'all') {
        return true;
      }

      if (this.filters.status === 'pending') {
        return status === 'Pending';
      }

      if (this.filters.status === 'new') {
        return status === 'New';
      }

      if (this.filters.status === 'in_progress') {
        return status === 'In Progress';
      }

      if (this.filters.status === 'completed') {
        return status === 'Completed';
      }

      if (this.filters.status === 'cancelled') {
        return status === 'Cancelled';
      }

      return true;
    },
    sortedUniqueValues(field) {
      const values = this.orders
        .map((item) => this.normalizeString(item[field]))
        .filter((value) => value !== '');

      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
    },
    parseDate(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    },
    withinDateRange(value, from, to) {
      const date = this.parseDate(value);
      if (!date) {
        return false;
      }

      if (from) {
        const fromDate = this.parseDate(from);
        if (!fromDate || date < fromDate) {
          return false;
        }
      }

      if (to) {
        const toDate = this.parseDate(to);
        if (!toDate) {
          return false;
        }

        const inclusiveToDate = new Date(toDate);
        inclusiveToDate.setHours(23, 59, 59, 999);

        if (date > inclusiveToDate) {
          return false;
        }
      }

      return true;
    },
    compareOrders(a, b) {
      const field = this.sort.field;
      const dir = this.sort.direction === 'asc' ? 1 : -1;

      if (field === 'total_qty') {
        return (this.toNumber(a.total_qty) - this.toNumber(b.total_qty)) * dir;
      }

      if (field === 'orderdate' || field === 'deliverydate') {
        const aDate = this.parseDate(a[field]);
        const bDate = this.parseDate(b[field]);
        const aValue = aDate ? aDate.getTime() : 0;
        const bValue = bDate ? bDate.getTime() : 0;
        return (aValue - bValue) * dir;
      }

      const aValue = this.normalizeString(a[field]).toLowerCase();
      const bValue = this.normalizeString(b[field]).toLowerCase();
      return aValue.localeCompare(bValue) * dir;
    },
    formatDate(value) {
      const date = this.parseDate(value);
      if (!date) {
        return 'N/A';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    groupByKey(keyName) {
      const grouped = this.filteredOrders.reduce((acc, order) => {
        const key = keyName === 'status'
          ? this.normalizedStatus(order[keyName])
          : (this.normalizeString(order[keyName]) || 'Unknown');

        if (!acc[key]) {
          acc[key] = { key, orders: 0, total_qty: 0 };
        }

        acc[key].orders += 1;
        acc[key].total_qty += this.toNumber(order.total_qty);
        return acc;
      }, {});

      return Object.values(grouped).sort((a, b) => b.total_qty - a.total_qty).slice(0, 15);
    },
    resetFilters() {
      this.filters = {
        search: '',
        status: 'all',
        region: '',
        clientname: '',
        garmentname: '',
        orderDateFrom: '',
        orderDateTo: '',
        deliveryDateFrom: '',
        deliveryDateTo: '',
        qtyMin: null,
        qtyMax: null,
      };

      this.sort = {
        field: 'deliverydate',
        direction: 'asc',
      };
    },
  },
};
</script>

<style scoped>
.orders-dashboard {
  padding-bottom: 1rem;
}

.dashboard-filters .label {
  margin-bottom: 0.3rem;
}

.summary-card {
  border: 1px solid #ececec;
  border-left: 4px solid #2684ff;
}

.orders-tab {
  overflow-x: auto;
}
</style>
